/* StooretPulse product page scripts: info pop-up, delivery estimate and ingredient tabs. Progressive enhancement only. */
(() => {
  'use strict';

  /* ---------- "Is this right for me?" pop-up ---------- */

  class InfoPopup extends HTMLElement {
    connectedCallback() {
      this.dialog = this.querySelector('[data-info-dialog]');
      this.opener = this.querySelector('[data-info-open]');
      if (!this.dialog || !this.opener) return;
      this.opener.addEventListener('click', () => this.dialog.showModal());
      this.querySelector('[data-info-close]')?.addEventListener('click', () => this.dialog.close());
      this.dialog.addEventListener('click', (e) => { if (e.target === this.dialog) this.dialog.close(); });
      this.dialog.addEventListener('close', () => this.opener.focus());
    }
  }
  customElements.define('info-popup', InfoPopup);

  /* ---------- Delivery estimate: dates counted from today ---------- */

  class DeliveryEstimate extends HTMLElement {
    connectedCallback() {
      const lang = document.documentElement.lang || undefined;
      const format = (days) => {
        const d = new Date();
        d.setDate(d.getDate() + days);
        return d.toLocaleDateString(lang, { day: 'numeric', month: 'short' });
      };
      const ship = Number(this.dataset.shipDays) || 0;
      const min = Number(this.dataset.minDays) || 1;
      const max = Math.max(min, Number(this.dataset.maxDays) || min);
      const shipEl = this.querySelector('[data-ship-date]');
      const deliveryEl = this.querySelector('[data-delivery-date]');
      if (shipEl) shipEl.textContent = ship === 0 ? this.dataset.today : ship === 1 ? this.dataset.tomorrow : format(ship);
      if (deliveryEl) deliveryEl.textContent = min === max ? format(min) : `${format(min)} – ${format(max)}`;
    }
  }
  customElements.define('delivery-estimate', DeliveryEstimate);

  /* ---------- Ingredient tabs ---------- */

  class IngredientTabs extends HTMLElement {
    connectedCallback() {
      this.tabs = [...this.querySelectorAll('[role="tab"]')];
      this.panels = [...this.querySelectorAll('[role="tabpanel"]')];
      this.tabs.forEach((tab, i) => {
        tab.addEventListener('click', () => this.select(i));
        tab.addEventListener('keydown', (e) => {
          const step = { ArrowRight: 1, ArrowLeft: -1 }[e.key];
          if (!step) return;
          e.preventDefault();
          const next = (i + step + this.tabs.length) % this.tabs.length;
          this.select(next);
          this.tabs[next].focus();
        });
      });
    }
    select(index) {
      this.tabs.forEach((tab, i) => {
        tab.setAttribute('aria-selected', String(i === index));
        tab.tabIndex = i === index ? 0 : -1;
      });
      this.panels.forEach((panel, i) => { panel.hidden = i !== index; });
    }
  }
  customElements.define('ingredient-tabs', IngredientTabs);

  // Theme editor: show the ingredient that is selected in the sidebar.
  document.addEventListener('shopify:block:select', (e) => {
    const tabs = e.target.closest('ingredient-tabs');
    if (tabs?.tabs) {
      const i = tabs.tabs.indexOf(e.target);
      if (i > -1) tabs.select(i);
    }
  });

  // Magnify the main image under the mouse on devices with a precise pointer.
  if (window.matchMedia('(hover: hover) and (pointer: fine)').matches) {
    const zoomSlide = (e) => {
      const slide = e.target.closest?.('[data-hover-zoom] .gallery__slide');
      if (!slide || !slide.querySelector(':scope > .gallery__image')) return null;
      return slide;
    };
    document.addEventListener('pointermove', (e) => {
      const slide = zoomSlide(e);
      if (!slide) return;
      const rect = slide.getBoundingClientRect();
      slide.style.setProperty('--zx', `${((e.clientX - rect.left) / rect.width) * 100}%`);
      slide.style.setProperty('--zy', `${((e.clientY - rect.top) / rect.height) * 100}%`);
      slide.classList.add('is-zooming');
    });
    document.addEventListener('pointerout', (e) => {
      const slide = zoomSlide(e);
      if (slide && !slide.contains(e.relatedTarget)) slide.classList.remove('is-zooming');
    });
  }

  // Customer reviews wall: masonry columns, more cards revealed as the visitor scrolls, photos open full size.
  class ReviewWall extends HTMLElement {
    connectedCallback() {
      this.perLoad = Number(this.dataset.perLoad) || 9;
      this.cards = [...this.querySelectorAll(':scope > .wall-card')];
      this.section = this.closest('.reviews-wall');
      this.more = this.section.querySelector('[data-wall-more]');
      this.dialog = this.section.querySelector('[data-wall-dialog]');
      this.classList.add('is-masonry');
      this.layout();

      this.more?.addEventListener('click', () => this.reveal());
      if ('IntersectionObserver' in window) {
        this.observer = new IntersectionObserver((entries) => {
          if (entries.some((e) => e.isIntersecting)) this.reveal();
        }, { rootMargin: '0px 0px 300px 0px' });
        this.observer.observe(this.more.parentElement);
      }
      this.updateMore();

      let width = this.clientWidth;
      this.resizeObserver = new ResizeObserver(() => {
        if (Math.abs(this.clientWidth - width) > 40 && this.columnCount() !== this.columns.length) this.layout();
        width = this.clientWidth;
      });
      this.resizeObserver.observe(this);
      this.addEventListener('load', () => this.queueLayout(), true);

      this.addEventListener('click', (e) => {
        const button = e.target.closest('[data-wall-zoom]');
        if (!button || !this.dialog) return;
        this.dialog.querySelector('[data-wall-zoom-image]').src = button.dataset.wallZoom;
        this.dialog.showModal();
      });
      this.dialog?.addEventListener('click', (e) => {
        if (e.target === this.dialog || e.target.closest('[data-wall-close]')) this.dialog.close();
      });
    }

    disconnectedCallback() {
      this.observer?.disconnect();
      this.resizeObserver?.disconnect();
    }

    columnCount() {
      const w = this.clientWidth;
      return w < 340 ? 1 : w < 860 ? 2 : w < 1180 ? 3 : 4;
    }

    visibleCards() {
      return this.cards.filter((card) => !card.hidden);
    }

    layout() {
      const count = this.columnCount();
      this.columns?.forEach((col) => col.remove());
      this.columns = Array.from({ length: count }, () => {
        const col = document.createElement('div');
        col.className = 'reviews-wall__column';
        this.append(col);
        return col;
      });
      this.place(this.visibleCards());
    }

    place(cards) {
      cards.forEach((card) => {
        const shortest = this.columns.reduce((a, b) => (b.offsetHeight < a.offsetHeight ? b : a));
        shortest.append(card);
      });
    }

    queueLayout() {
      cancelAnimationFrame(this.frame);
      this.frame = requestAnimationFrame(() => this.layout());
    }

    reveal() {
      const next = this.cards.filter((card) => card.hidden).slice(0, this.perLoad);
      if (!next.length) return;
      next.forEach((card) => {
        card.hidden = false;
        card.classList.add('is-revealed');
      });
      this.place(next);
      this.updateMore();
    }

    updateMore() {
      const remaining = this.cards.some((card) => card.hidden);
      if (this.more) this.more.hidden = !remaining || Boolean(this.observer);
      if (!remaining) this.observer?.disconnect();
    }
  }
  customElements.define('review-wall', ReviewWall);
})();
